import {
  faBell,
  faBolt,
  faBox,
  faCalendarDay,
  faClipboardCheck,
  faClockNine,
  faDumbbell,
  faGear,
  faGrid2,
  faRankingStar,
  faUserDoctor,
  faUserPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar } from 'src/components/Avatar';
import { Badge } from 'src/components/Badge';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { Theme } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import {
  fetchNotifications,
  readNotification,
} from 'src/models/notification/notification';
import { isHQPlatform } from 'src/models/platform/utils';
import { hasLoggingDisabled } from 'src/models/platform/utils';
import { isTrainer } from 'src/models/user/utils';
import TimeClock from 'src/pages/TimeTracking/TimeClock';
import { useTimeClockPage } from 'src/pages/TimeTracking/useTimeClockPage';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import { UserDropdown } from 'src/shared/UserDropdown';
import { Timestamp } from 'src/types/utilityTypes';
import { formatListDateAndTime } from 'src/utils/dateFormatter';

export interface ActionsMenuProps {
  size?: 'small' | 'medium';
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  size = 'medium',
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'actionsMenu' });
  const { user, session } = useSession();
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isHQ = isHQPlatform(session);

  if (!isTrainer(user)) {
    return (
      <Stack
        justifyContent="space-between"
        direction={size === 'small' ? 'row-reverse' : 'row'}
        gap={size === 'small' ? 1 : 2}
      >
        {!user?.hasDisabledLogging &&
          !hasLoggingDisabled(session) &&
          !mdDown && (
            <Button
              component={Link}
              href={externalLinks.v2Url('/v2/workouts/new')}
              target="_blank"
              color="inherit"
              variant="bordered"
              startIcon={<Icon size="medium" icon={faDumbbell} />}
            >
              {t('logWorkout')}
            </Button>
          )}
        <Tooltip title={t('booking')}>
          <IconButton
            component={Link}
            href={externalLinks.v3Url('/booking')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon color="action" size="medium" icon={faCalendarDay} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('packages')}>
          <IconButton
            component={Link}
            href={externalLinks.v3Url('/packages')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon color="action" size="medium" icon={faBox} />
          </IconButton>
        </Tooltip>
        <NotificationsMenu />
        <UserDropdown />
      </Stack>
    );
  }

  return (
    <Stack
      justifyContent="space-between"
      direction={size === 'small' ? 'row-reverse' : 'row'}
      gap={size === 'small' ? 1 : 2}
    >
      {!isHQ && (
        <>
          <PermissionCheck can="clockIn" object={user}>
            <TimeClockMenu />
          </PermissionCheck>
          <QuickActionsMenu />
        </>
      )}
      <LauncherMenu />
      <NotificationsMenu />
      <UserDropdown />
    </Stack>
  );
};

export const TimeClockMenu: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'actionsMenu' });
  const { user } = useSession();
  const { logEntry } = useTimeClockPage(user);
  const clockedIn = logEntry ? !logEntry?.endDate : false;

  return (
    <DropdownMenu
      drawerOnMobile
      closeOnContentClick={false}
      trigger={
        <IconButton>
          <Badge variant="dot" color="primary" invisible={!clockedIn}>
            <Icon color="action" size="medium" icon={faClockNine} />
          </Badge>
        </IconButton>
      }
    >
      <Typography variant="h5" px={2} pb={2} pt={2}>
        {t('timeClock')}
      </Typography>
      <Box
        sx={{
          minWidth: '350px',
          p: 2,
        }}
      >
        <TimeClock
          onSubmit={() => {}}
          user={user}
          clockedIn={clockedIn}
          logEntry={logEntry}
        />
      </Box>
    </DropdownMenu>
  );
};

export const QuickActionsMenu: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'actionsMenu' });
  return (
    <DropdownMenu
      drawerOnMobile
      trigger={
        <IconButton>
          <Icon color="action" size="medium" icon={faBolt} />
        </IconButton>
      }
    >
      {/* <Typography variant="h5" px={2} pb={2} pt={1}>
        Quick Actions
      </Typography> */}
      <Box sx={{ px: 1, minWidth: 220 }}>
        <ListItem
          component={RouterLink}
          to={'/clients/new'}
          icon={faUserPlus}
          primary={t('newClient')}
          clickable
          rounded
          sx={{ py: 1, my: 1 }}
        />
      </Box>
    </DropdownMenu>
  );
};

export const LauncherMenu: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'actionsMenu' });

  return (
    <DropdownMenu
      drawerOnMobile
      trigger={
        <IconButton>
          <Icon color="action" size="medium" icon={faGrid2} />
        </IconButton>
      }
    >
      {/* <Typography variant="h5" px={2} pb={2} pt={0}>
        Launch
      </Typography> */}
      <Box sx={{ px: 1, minWidth: 220 }}>
        <ListItem
          component={Link}
          href={externalLinks.v3Url('/booking')}
          target="_blank"
          icon={faCalendarDay}
          primary={t('booking')}
          clickable
          rounded
          sx={{ my: 1 }}
        />
        <ListItem
          component={Link}
          href={externalLinks.v3Url('/leaderboards')}
          target="_blank"
          icon={faRankingStar}
          primary={t('leaderboards')}
          clickable
          rounded
          sx={{ my: 1 }}
        />
        <ListItem
          component={Link}
          href={'/ex4/leaderboards'}
          icon={faGear}
          primary={t('manageLeaderboards')}
          clickable
          rounded
          sx={{ my: 1 }}
        />
        <ListItem
          component={Link}
          href={'/ex4/admin/modal-alerts'}
          icon={faGear}
          primary={t('manageAppAlerts')}
          clickable
          rounded
          sx={{ my: 1 }}
        />
        <ListItem
          component={Link}
          href={externalLinks.v3Url('/packages')}
          target="_blank"
          icon={faBox}
          primary={t('packages')}
          clickable
          rounded
          sx={{ my: 1 }}
        />
        <ListItem
          component={Link}
          href={'/ex4/me/assessments'}
          target="_blank"
          icon={faClipboardCheck}
          primary={t('assessments')}
          clickable
          rounded
          sx={{ my: 1 }}
        />
        <ListItem
          component={Link}
          href={externalLinks.accountManager()}
          target="_blank"
          icon={faUserDoctor}
          primary={t('iPadAccountManager')}
          clickable
          rounded
          sx={{ my: 1 }}
        />
      </Box>
    </DropdownMenu>
  );
};

export const NotificationsMenu: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'actionsMenu' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const queryClient = useQueryClient();
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],

    queryFn: () => {
      return fetchNotifications();
    },

    refetchOnWindowFocus: false,
    gcTime: 10000,
  });

  interface NotificationParams {
    start: Timestamp;
    end?: Timestamp;
  }

  const readNotificationMutation = useMutation({
    mutationFn: ({ start, end }: NotificationParams) => {
      return readNotification({ start, end });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return (
    <DropdownMenu
      drawerOnMobile
      trigger={
        <Badge
          invisible={!notifications?.length}
          variant="dot"
          overlap="circular"
        >
          <IconButton>
            <Icon color="action" size="medium" icon={faBell} />
          </IconButton>
        </Badge>
      }
    >
      <Box
        sx={{
          minWidth: '320px',
          maxWidth: '600px',
        }}
      >
        <Stack direction="row" alignItems="center" px={2} py={2} pb={1}>
          <Typography variant="h5">{t('notifications')}</Typography>
        </Stack>

        <Box
          sx={{
            p: 1,
          }}
        >
          {!!notifications?.length &&
            notifications.map((notification) => (
              <ListItem
                clickable
                onClick={() => {
                  const link = notification.link.replace('wtcom://', '/');
                  window.open(link, '_blank');
                  readNotificationMutation.mutate({
                    start: notification.time,
                  });
                }}
                key={notification.string}
                avatar={<Avatar src={notification.userimage} />}
                secondary={formatListDateAndTime(notification.time)}
                centerIndent={false}
                rounded
                primary={
                  <div
                    dangerouslySetInnerHTML={{ __html: notification.string }}
                  />
                }
              />
            ))}

          {!notifications?.length && (
            <Typography
              color="text.secondary"
              variant="subtitle1"
              px={1}
              pt={1}
              pb={2}
            >
              {tCommon('messages.noUnreadNotifications')}
            </Typography>
          )}
        </Box>

        <Stack direction="row-reverse" pb={1} px={2} gap={2}>
          <Button
            size="small"
            onClick={() =>
              window.open(externalLinks.v2Url('/notifications'), '_blank')
            }
            disabled={!notifications?.length}
          >
            {tCommon('buttons.showAll')}
          </Button>
          <Button
            size="small"
            disabled={!notifications?.length}
            onClick={() => {
              if (notifications?.length) {
                readNotificationMutation.mutate({
                  end: notifications[0]?.time,
                  start: notifications[notifications.length - 1]?.time,
                });
              }
            }}
          >
            {tCommon('buttons.markAsRead')}
          </Button>
        </Stack>
      </Box>
    </DropdownMenu>
  );
};
